import { NgClass, NgForOf, NgIf, NgSwitch } from '@angular/common';
import { AfterViewInit, Component, EventEmitter, Input, OnInit, Output, ViewChild, ViewEncapsulation } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatOption } from '@angular/material/core';
import { MatSelect } from '@angular/material/select';
import { MatTooltip } from '@angular/material/tooltip';
import { combineLatest, Observable, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';
import { ActionData, ActionsOperators, Entities } from '../data-rules-model';
import { DataRulesService } from '../data-rules.service';
import { AutocompleteComponent } from '../lib/autocomplete/autocomplete.component';
import { ICustomPositioningConfig } from '../lib/custom-component-overlay.directive';
import { StaticOptionsProvider } from '../lib/option-items-providers';
import { IEntityItem, PairedAutocompleteComponent } from '../lib/paired-autocomplete/paired-autocomplete.component';
import { IActionFieldMeta, MetaDataService, MetaSource } from '../meta-data.service';
import { transitionNewRowInOut } from '../rule/kyloe-animations';
import { ActionMessageDescriptionComponent } from './action-message-description/action-message-description.component';
import { AddRemoveComponent } from './add-remove/add-remove.component';
import { AddressActionComponent } from './address-action/address-action.component';
import { AllowedActions } from './allowed-actions';
import { AppendEntriesComponent } from './append-entries/append-entries.component';
import { AppendComponent } from './append/append.component';
import { ChangeComponent } from './change/change.component';
import { ConcatenateComponent } from './concatenate/concatenate.component';
import { CopyMoveSwapComponent } from './copy-move-swap/copy-move-swap.component';
import { CountComponent } from './count/count.component';
import { MathOperatorComponent } from './math-operations/math-operation.component';
import { PrependComponent } from './prepend/prepend.component';
import { ReplaceRelativeComponent } from './replace-relative/replace-relative.component';
import { ReplaceComponent } from './replace/replace.component';
import { StringformatComponent } from './stringformat/stringformat.component';
import { SubstituteComponent } from './substitute/substitute.component';

interface IFieldToChangeParameters {
  fieldName: string;
  src: string;
  dst: string;
}

@Component({
  selector: 'app-action-x',
  templateUrl: './action.component.html',
  styleUrls: ['./action.component.scss'],
  animations: [transitionNewRowInOut],
  imports: [
    ReactiveFormsModule,
    NgClass,
    NgSwitch,
    PairedAutocompleteComponent,
    MatSelect,
    MatOption,
    MatTooltip,
    NgForOf,
    AddressActionComponent,
    AppendComponent,
    PrependComponent,
    SubstituteComponent,
    MathOperatorComponent,
    ReplaceComponent,
    ReplaceRelativeComponent,
    NgIf,
    StringformatComponent,
    CopyMoveSwapComponent,
    CountComponent,
    ChangeComponent,
    AddRemoveComponent,
    AppendEntriesComponent,
    ConcatenateComponent,
    ActionMessageDescriptionComponent
  ],
  encapsulation: ViewEncapsulation.None
})
export class ActionComponent implements OnInit, AfterViewInit {
  @Input() actionData: ActionData;    // this should not be here as the form is the model of truth
  @Input() actionForm: FormGroup;
  @Input() primaryEntity: Entities;

  dataSourceType = MetaSource.ACTION;

  actionTypeControl: AbstractControl;

  sourceControl: AbstractControl;
  sourceDescriptorControl: AbstractControl;
  sourceTypeControl: AbstractControl;

  destinationControl: AbstractControl;
  destinationDescriptorControl: AbstractControl;
  destinationTypeControl: AbstractControl;

  valueControl: AbstractControl;

  fieldToChangeControl: AbstractControl;
  fieldToChangeDescriptorControl: AbstractControl;
  fieldToChangeTypeControl: AbstractControl;

  fieldToChange = '';

  fieldToChangeSubscription: Subscription;
  fieldToChangeDescriptorSubscription: Subscription;

  actionOperators: ActionsOperators[] = [];
  actionTypePlaceholder = 'Choose action...';
  invalidControls: string[] = [];

  overlayPositioningConfig: ICustomPositioningConfig = {
    originX: 'center',
    originY: 'bottom',
    overlayX: 'center',
    overlayY: 'top',
    offsetY: 8,
    offsetX: -20
  };

  entitiesOptions: IEntityItem[];
  public actionOps: typeof ActionsOperators = ActionsOperators;

  @ViewChild('fieldName')
  private fieldPickerComponent: AutocompleteComponent;

  constructor(
    private metaDataService: MetaDataService,
    public dataRulesService: DataRulesService
  ) {
  }

  ngOnInit() {
    this.createControls();

    this.metaDataService.reduceMetaToEntitiesOptions(this.primaryEntity, MetaSource.ACTION).subscribe(
      opts => {
        this.entitiesOptions = opts;
      }
      , error => {
        console.error(error);
      });
    this.subscribeToChanges();
  }

  ngAfterViewInit() {
    const f = ActionComponent.isSingleAction(this.actionTypeControl.value)
      ? this.destinationDescriptorControl.value
      : this.sourceDescriptorControl.value;
    this.getMeta(f)
      .subscribe(filteredMeta => {
        const options = filteredMeta.map(fm => ({name: fm.fieldName, label: fm.label}));
        const optionsProvider = new StaticOptionsProvider(options);
        this.fieldPickerComponent.setOptionsProvider(optionsProvider, this.fieldToChangeControl.value);
      });
  }

  protected getMeta(descriptorControl: string | null): Observable<IActionFieldMeta[]> {
    return this.metaDataService.filterMetaBySecondaryDescriptor(
      this.primaryEntity,
      descriptorControl,
      MetaSource.ACTION
    )
      .pipe(
        map(v => v as IActionFieldMeta[])
      );
  }

  private createControls(): void {
    this.actionForm.removeControl('type');
    this.actionForm.removeControl('source');
    this.actionForm.removeControl('sourceDescriptor');
    this.actionForm.removeControl('sourceType');
    this.actionForm.removeControl('destination');
    this.actionForm.removeControl('destinationDescriptor');
    this.actionForm.removeControl('destinationType');
    this.actionForm.removeControl('value');

    this.actionTypeControl = new FormControl(this.actionData.type, Validators.required);

    this.sourceControl = new FormControl(this.actionData.source);
    this.sourceDescriptorControl = new FormControl(this.actionData.sourceDescriptor);
    this.sourceTypeControl = new FormControl(this.actionData.sourceType);

    this.destinationControl = new FormControl(this.actionData.destination);
    this.destinationDescriptorControl = new FormControl(this.actionData.destinationDescriptor);
    this.destinationTypeControl = new FormControl(this.actionData.destinationType);

    this.valueControl = new FormControl(this.actionData.value);

    this.fieldToChangeControl = new FormControl();
    this.fieldToChangeDescriptorControl = new FormControl();
    this.fieldToChangeTypeControl = new FormControl();

    this.actionForm.addControl('type', this.actionTypeControl);
    this.actionForm.addControl('source', this.sourceControl);
    this.actionForm.addControl('sourceDescriptor', this.sourceDescriptorControl);
    this.actionForm.addControl('sourceType', this.sourceTypeControl);
    this.actionForm.addControl('destination', this.destinationControl);
    this.actionForm.addControl('destinationDescriptor', this.destinationDescriptorControl);
    this.actionForm.addControl('destinationType', this.destinationTypeControl);
    this.actionForm.addControl('value', this.valueControl);

    const action = this.actionData.type;

    if (this.actionTypeControl.value) {
      this.actionTypeControl.disable();
    }
    if (ActionComponent.isSingleAction(action))
      this.makeSelectedFieldDestination(true);
    else
      this.makeSelectedFieldSource(true);

    this.sourceControlValueChangeHandler(this.sourceControl.value);
    this.destinationControlValueChangeHandler(this.destinationControl.value);
    this.fieldToChangeControlValueChangeHandler({
      fieldName: this.fieldToChangeControl.value,
      src: this.sourceControl.value,
      dst: this.destinationControl.value
    });
  }

  private subscribeToChanges(): void {
    this.sourceControl.valueChanges.subscribe(value => this.sourceControlValueChangeHandler(value));
    this.sourceDescriptorControl.valueChanges.subscribe(value => this.sourceDescriptorValueChangeHandler(value));
    this.actionTypeControl.valueChanges.subscribe(value => this.typeControlValueChangeHandler(value));
    this.destinationControl.valueChanges.subscribe(value => this.destinationControlValueChangeHandler(value));
    this.destinationDescriptorControl.valueChanges.subscribe(value => this.destinationDescriptorValueChangeHandler(value));
    this.fieldToChangeSubscription = this.fieldToChangeControl.valueChanges.subscribe(value =>
      this.fieldToChangeControlValueChangeHandler({
        fieldName: value,
        src: this.sourceControl.value,
        dst: this.destinationControl.value
      })
    );
    this.fieldToChangeDescriptorSubscription = this.fieldToChangeDescriptorControl.valueChanges.subscribe(value =>
      this.fieldToChangeDescriptorControlValueChangeHandler(value)
    );
  }

  private typeControlValueChangeHandler(action: any): void {
    if (!action)
      return;
    if (ActionComponent.isSingleAction(action)) {
      this.makeSelectedFieldDestination();
      this.resetSource();
    } else {
      this.makeSelectedFieldSource();
    }
    this.actionTypeControl.disable({ emitEvent: false });
  }

  private static isSingleAction(action: string): boolean {
    return action === 'NULLIFY'
      || action === 'REPLACE'
      || action === 'REPLACE_RELATIVE'
      || action === 'ADD'
      || action === 'REMOVE'
      || action === 'CHANGE'
      || action === 'APPEND'
      || action === 'FORMAT'
      || action === 'SUBSTITUTE'
      || action === 'MATHS'
      || action === 'PREPEND'
      || action === 'APPEND_ENTRY'
      || action === 'CONCATENATE';
  }

  private makeSelectedFieldDestination(isInitial: boolean = false): void {

    // copying the value from the 'source' to the source
    if (!this.destinationControl.value) {
      this.destinationControl.patchValue(this.fieldToChangeControl.value); // setting the destination 'field'
      this.destinationDescriptorControl.patchValue(this.fieldToChangeDescriptorControl.value); // setting the destination 'entity'
      this.destinationTypeControl.patchValue(this.fieldToChangeTypeControl.value);
    }
    if (isInitial && this.fieldToChangeDescriptorControl.value !== this.destinationDescriptorControl.value) {
      this.fieldToChangeDescriptorControl.patchValue(this.destinationDescriptorControl.value);
    }

    this.fieldToChangeControl = this.destinationControl;
    this.fieldToChangeTypeControl = this.destinationTypeControl;

    if (this.fieldToChangeSubscription) {
      this.fieldToChangeSubscription.unsubscribe();
    }
    this.fieldToChangeSubscription = this.fieldToChangeControl.valueChanges.subscribe(value =>
      this.fieldToChangeControlValueChangeHandler({
        fieldName: value,
        src: this.sourceControl.value,
        dst: this.destinationControl.value
      })
    );

    if (this.fieldToChangeDescriptorSubscription) {
      this.fieldToChangeDescriptorSubscription.unsubscribe();
    }
    this.fieldToChangeDescriptorSubscription = this.fieldToChangeDescriptorControl.valueChanges.subscribe(value =>
      this.fieldToChangeDescriptorControlValueChangeHandler(value)
    );

    this.fieldToChange = 'destination';

    this.fieldToChangeControlValueChangeHandler({
      fieldName: this.fieldToChangeControl.value,
      src: this.sourceControl.value,
      dst: this.destinationControl.value
    });
  }

  fieldToChangeCleared() {
    this.makeSelectedFieldSource();
  }

  private makeSelectedFieldSource(isInitial: boolean = false): void {
    if (!this.sourceControl.value) {
      this.sourceControl.patchValue(this.fieldToChangeControl.value);
      this.sourceDescriptorControl.patchValue(this.fieldToChangeDescriptorControl.value);
      this.sourceTypeControl.patchValue(this.fieldToChangeTypeControl.value);
    }
    if (isInitial && this.fieldToChangeDescriptorControl.value !== this.sourceDescriptorControl.value) {
      this.fieldToChangeDescriptorControl.patchValue(this.sourceDescriptorControl.value);
    }

    this.fieldToChangeControl = this.sourceControl;
    this.fieldToChangeTypeControl = this.sourceTypeControl;

    if (this.fieldToChangeSubscription) {
      this.fieldToChangeSubscription.unsubscribe();
    }
    this.fieldToChangeSubscription = this.fieldToChangeControl.valueChanges.subscribe(value =>
      this.fieldToChangeControlValueChangeHandler({
        fieldName: value,
        src: this.sourceControl.value,
        dst: this.destinationControl.value
      })
    );
    if (this.fieldToChangeDescriptorSubscription) {
      this.fieldToChangeDescriptorSubscription.unsubscribe();
    }
    this.fieldToChangeDescriptorSubscription = this.fieldToChangeDescriptorControl.valueChanges.subscribe(value =>
      this.fieldToChangeDescriptorControlValueChangeHandler(value)
    );

    this.fieldToChange = 'source';

    this.fieldToChangeControlValueChangeHandler({
      fieldName: this.fieldToChangeControl.value,
      src: this.sourceControl.value,
      dst: this.destinationControl.value
    });
  }

  private sourceControlValueChangeHandler(fieldName: string): void {
    this.getMeta(this.sourceDescriptorControl.value)
      .subscribe(filteredMeta => {
        const meta = filteredMeta.find(fm => fm.fieldName === fieldName);
        this.sourceTypeControl.setValue(meta ? meta.metaType : null);
      });
  }

  private sourceDescriptorValueChangeHandler(descriptor: string): void {

  }

  private destinationControlValueChangeHandler(fieldName: string): void {
    this.getMeta(this.destinationDescriptorControl.value)
      .subscribe(filteredMeta => {
        const meta = filteredMeta.find(fm => fm.fieldName === fieldName);
        this.destinationTypeControl.setValue(meta ? meta.metaType : null);
      });
  }

  private destinationDescriptorValueChangeHandler(descriptor: string): void {

  }

  private resetSource() {
    this.sourceControl.setValue('', {emitEvent: false});
    this.sourceDescriptorControl.setValue('', {emitEvent: false});
    this.sourceTypeControl.setValue(null, {emitEvent: false});
    this.valueControl.setValue(null, {emitEvent: false});
  }

  private fieldToChangeControlValueChangeHandler(
    {fieldName, src, dst}: IFieldToChangeParameters
  ): void {
    // blank everything if we get rid of the 'field to change'.
    if (!fieldName) {
      if (src) {
        src = null;
        this.sourceControl.setValue(null);
      }

      if (dst) {
        dst = null;
        this.destinationControl.setValue(null);
      }

      this.sourceTypeControl.setValue(null);
      this.destinationTypeControl.setValue(null);

      this.valueControl.setValue(null);
      if (this.actionTypeControl.value !== this.dataRulesService.actionOpName(ActionsOperators.DELETE)) {
        this.actionTypeControl.setValue(null, {emitEvent: false});
      }
    }

    console.log(`fieldName: ${fieldName} - src: ${src} - dst: ${dst}`);

    combineLatest([
      this.getMeta(this.fieldToChangeDescriptorControl.value),
      this.getMeta(this.sourceDescriptorControl.value),
      this.getMeta(this.destinationDescriptorControl.value)
    ]).subscribe(([
                    metaFields,
                    srcMetaFields,
                    dstMetaFields
                  ]) => {

      const meta = metaFields.find(fm => fm.fieldName === fieldName);
      const srcMeta = srcMetaFields.find(fm => fm.fieldName === src);
      const dstMeta = dstMetaFields.find(fm => fm.fieldName === dst);

      this.actionOperators = meta ? ActionComponent.allowedActions(meta, srcMeta, dstMeta) : [];
      this.actionTypePlaceholder = fieldName && !this.actionOperators.length ? 'No actions available' : 'Choose action...';
    });
  }

  private fieldToChangeDescriptorControlValueChangeHandler(descriptor: string, initial?: boolean): void {
    this.metaDataService.filterMetaBySecondaryDescriptor(this.primaryEntity, descriptor, MetaSource.ACTION)
      .subscribe((filteredMeta: IActionFieldMeta[]) => {

        if (!initial) {
          this.nullifyActionFormValues();

          if (ActionComponent.isSingleAction(this.actionTypeControl.value))
            this.makeSelectedFieldDestination(true);
          else
            this.makeSelectedFieldSource(true);

          if (this.fieldToChangeSubscription) {
            this.fieldToChangeSubscription.unsubscribe();
          }
          this.fieldToChangeSubscription = this.fieldToChangeControl.valueChanges.subscribe(value =>
            this.fieldToChangeControlValueChangeHandler({
              fieldName: value,
              src: this.sourceControl.value,
              dst: this.destinationControl.value
            })
          );

        }

        const options = filteredMeta.map(fm => ({name: fm.fieldName, label: fm.label}));
        const optionsProvider = new StaticOptionsProvider(options);
        this.fieldPickerComponent.setOptionsProvider(optionsProvider, this.fieldToChangeControl.value);

      }, error => {
        console.error(error);
      });
  }

  private nullifyActionFormValues(): void {
    this.fieldToChangeControl.setValue(null);

    const data = new ActionData(undefined);

    this.sourceControl.setValue(data.source, {emitEvent: false});
    this.sourceDescriptorControl.setValue(data.source, {emitEvent: false});
    this.destinationControl.setValue(data.destination, {emitEvent: false});
    this.destinationDescriptorControl.setValue(data.destinationDescriptor, {emitEvent: false});

    this.actionTypeControl.setValue(data.type, {emitEvent: false});

    this.sourceTypeControl.setValue(data.sourceType, {emitEvent: false});
    this.destinationTypeControl.setValue(data.destinationType, {emitEvent: false});

    this.valueControl.setValue(data.value, {emitEvent: false});
  }

  private static allowedActions(selectedFieldMeta: IActionFieldMeta,
                                srcMeta: IActionFieldMeta,
                                dstMeta: IActionFieldMeta): ActionsOperators[] {
    return [
      ActionsOperators.ADD,
      ActionsOperators.APPEND,
      ActionsOperators.CHANGE,
      ActionsOperators.COPY,
      ActionsOperators.FORMAT,
      ActionsOperators.MOVE,
      ActionsOperators.NULLIFY,
      ActionsOperators.PREPEND,
      ActionsOperators.REMOVE,
      ActionsOperators.REPLACE,
      ActionsOperators.REPLACE_RELATIVE,
      ActionsOperators.SUBSTITUTE,
      ActionsOperators.MATHS,
      ActionsOperators.SWAP,
      ActionsOperators.APPEND_ENTRY,
      ActionsOperators.COUNT,
      ActionsOperators.CONCATENATE,
    ].sort().filter(op => AllowedActions.actionAllowed(op, selectedFieldMeta, srcMeta, dstMeta));
  }
}
